import React, { useEffect, useRef, useState } from 'react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import LocationPinIcon from './LocationPin.svg';
import { ZOOM_DEFAULTS } from './LocationFinderTypes';
import type { Location } from './LocationFinderTypes';

// TÜV NORD colors from Tailwind config
const colors = {
    blue: '#001ed2',
    darkblue: '#00003c',
    white: '#ffffff',
};

interface GoogleMapProps {
    locations: Location[];
    center: { lat: number; lng: number };
    selectedLocation: Location | null;
    onLocationSelect: (location: Location) => void;
    searchPosition?: { lat: number; lng: number } | null;
    forceZoomToLocation?: boolean;
    useZoomlevel?: boolean;
    zoomSingleMarker?: number;
    zoomSelectedLocation?: number;
    zoomInitial?: number;
}

const GoogleMap: React.FC<GoogleMapProps> = ({
    locations,
    center,
    selectedLocation,
    onLocationSelect,
    searchPosition,
    forceZoomToLocation = false,
    useZoomlevel = false,
    zoomSingleMarker = ZOOM_DEFAULTS.SINGLE_MARKER,
    zoomSelectedLocation = ZOOM_DEFAULTS.SELECTED_LOCATION,
    zoomInitial = ZOOM_DEFAULTS.INITIAL,
}) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<google.maps.Map | null>(null);
    const markerClustererRef = useRef<MarkerClusterer | null>(null);
    const allMarkersRef = useRef<Map<number, google.maps.marker.AdvancedMarkerElement>>(new Map());
    // The map instance appears asynchronously (initializeMap polls until the Google script is there).
    // Effects that position the camera bail out while it is missing, and their dependencies do not
    // change afterwards — on mobile the map mounts only when the user switches to the map tab, i.e.
    // always after the search. This flag lets them run once the instance exists.
    const [mapReady, setMapReady] = useState(false);

    // Initialize map only once
    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        const initializeMap = () => {
            if (!window.google?.maps) {
                setTimeout(initializeMap, 100);
                return;
            }

            // Force layout reflow to ensure dimensions are calculated
            mapRef.current!.style.display = 'block';
            void mapRef.current!.offsetHeight; // Force reflow

            const map = new window.google.maps.Map(mapRef.current!, {
                center: center,
                zoom: zoomInitial,
                mapTypeControl: true,
                streetViewControl: true,
                fullscreenControl: true,
                mapId: 'tuev-nord-location-finder', // Required for AdvancedMarkerElement
            });

            // Trigger resize event after map initialization
            setTimeout(() => {
                google.maps.event.trigger(map, 'resize');
                // Same reasoning as the center effect below: with a search active the marker effect
                // owns the camera. On mobile the map mounts only after a search, so this delayed
                // re-centring would undo the fit every time.
                if (!searchPosition) {
                    map.setCenter(center);
                }
            }, 100);

            mapInstanceRef.current = map;
            setMapReady(true);
        };

        initializeMap();
        // Re-runs are no-ops once the instance exists (guard above); the dependency only keeps the
        // searchPosition check inside initializeMap reading a current value.
    }, [center, zoomInitial, searchPosition]); // Initialize once

    // Create markers once when locations first load
    useEffect(() => {
        const map = mapInstanceRef.current;

        if (!map || !window.google || locations.length === 0) {
            return;
        }

        // Only create markers if we haven't created them yet
        locations.forEach((location) => {
            if (allMarkersRef.current.has(location.uid)) {
                return; // Marker already exists
            }

            // Create marker content at default size (selection effect handles resizing)
            const markerContent = document.createElement('div');
            const img = document.createElement('img');
            img.src = LocationPinIcon;
            img.width = 60;
            img.height = 72;
            img.alt = location.title;
            img.style.cursor = 'pointer';
            markerContent.appendChild(img);

            // Create a wrapper object that provides getPosition for MarkerClusterer
            const position = new google.maps.LatLng(location.latitude, location.longitude);
            const marker = new google.maps.marker.AdvancedMarkerElement({
                map: null, // Initially not on map
                position: position,
                title: location.title,
                content: markerContent,
                zIndex: 1000 + location.uid, // Use uid instead of index
            });

            // Add getPosition method for MarkerClusterer compatibility
            (marker as any).getPosition = () => position;

            // Store location data on marker for later access
            (marker as any)._location = location;
            (markerContent as any)._location = location;

            // Add click listener directly to the content element
            markerContent.style.cursor = 'pointer';
            markerContent.addEventListener('click', (e) => {
                e.stopPropagation();

                // Call the selection callback
                if (onLocationSelect && typeof onLocationSelect === 'function') {
                    onLocationSelect(location);
                }
            });

            // Store marker in the all markers map
            allMarkersRef.current.set(location.uid, marker);

            // Initially hide the marker
            marker.map = null;
        });
    }, [mapReady, locations, onLocationSelect]); // Create markers when locations change

    // Update visible markers based on filtered locations
    useEffect(() => {
        const map = mapInstanceRef.current;

        if (!map || !window.google) {
            return;
        }

        // Clear clusterer
        if (markerClustererRef.current) {
            markerClustererRef.current.clearMarkers();
            markerClustererRef.current = null;
        }

        // Update which markers are visible
        const visibleMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
        const bounds = new google.maps.LatLngBounds();

        locations.forEach((location) => {
            const marker = allMarkersRef.current.get(location.uid);
            if (!marker) return;

            if (location.visibility === false) {
                // Hide marker
                marker.map = null;
            } else {
                // Show marker
                marker.map = map;
                visibleMarkers.push(marker);
                bounds.extend(marker.position as google.maps.LatLng);
            }
        });

        // Create marker clusterer with visible markers
        if (visibleMarkers.length > 0) {
            // Create custom renderer for clusters
            const renderer = {
                render: ({ count, position }: { count: number; position: google.maps.LatLng }) => {
                    // Create cluster marker content
                    const clusterContent = document.createElement('div');
                    clusterContent.innerHTML = `
                        <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="32" cy="32" r="32" fill="${colors.blue}" opacity="0.3"/>
                            <circle cx="32" cy="32" r="24" fill="${colors.blue}" opacity="0.5"/>
                            <circle cx="32" cy="32" r="16" fill="${colors.blue}" opacity="1"/>
                            <text x="32" y="38" text-anchor="middle" font-family="Arial, sans-serif" font-size="14" font-weight="bold" fill="${colors.white}">${count}</text>
                        </svg>
                    `;
                    clusterContent.style.cursor = 'pointer';

                    return new google.maps.marker.AdvancedMarkerElement({
                        map,
                        position,
                        content: clusterContent,
                        zIndex: 1000,
                    });
                },
            };

            // Create marker clusterer with AdvancedMarkerElement
            markerClustererRef.current = new MarkerClusterer({
                map,
                markers: visibleMarkers,
                renderer,
            });
        }

        // Fit bounds if we have visible markers
        // Skip when forceZoomToLocation is set — the selected-location effect handles zoom
        if (visibleMarkers.length > 0 && !forceZoomToLocation) {
            // If searchPosition exists (user selected a location), add it to bounds
            if (searchPosition) {
                const searchLatLng = new google.maps.LatLng(searchPosition.lat, searchPosition.lng);
                bounds.extend(searchLatLng);
            }

            if (visibleMarkers.length === 1 && !searchPosition) {
                const pos = visibleMarkers[0].position as google.maps.LatLng;
                map.setCenter(pos);
                const singleLoc = (visibleMarkers[0] as any)._location as Location | undefined;
                map.setZoom(useZoomlevel && singleLoc?.zoomlevel != null ? singleLoc.zoomlevel : zoomSingleMarker);
            } else {
                // Add padding to ensure markers aren't at edge
                map.fitBounds(bounds, 60);
            }
        }
    }, [mapReady, locations, searchPosition, forceZoomToLocation, useZoomlevel, zoomSingleMarker]); // Update visibility when locations or search position changes

    // Track previously selected marker to avoid iterating all markers
    const prevSelectedUidRef = useRef<number | null>(null);

    // Update marker appearance when selection changes — O(1) instead of O(n)
    useEffect(() => {
        if (!window.google) return;

        const resizeMarker = (uid: number, width: number, height: number) => {
            const marker = allMarkersRef.current.get(uid);
            if (!marker) return;
            const img = (marker.content as HTMLElement)?.querySelector('img');
            if (img && (img.width !== width || img.height !== height)) {
                img.width = width;
                img.height = height;
            }
        };

        // Shrink previously selected marker back to default
        if (prevSelectedUidRef.current != null) {
            resizeMarker(prevSelectedUidRef.current, 60, 72);
        }

        // Enlarge newly selected marker
        if (selectedLocation) {
            resizeMarker(selectedLocation.uid, 80, 96);
        }

        prevSelectedUidRef.current = selectedLocation?.uid ?? null;
    }, [selectedLocation]);

    // Center map when center prop changes.
    // While a search is active the marker effect above already framed the camera on the search
    // position together with the results, and it runs first. Re-centring on the search position
    // alone would keep that zoom but push the results out of view — which is what happened when a
    // town centre and its only nearby station sit a few kilometres apart.
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map || !window.google || searchPosition) return;

        map.setCenter(center);
    }, [center, searchPosition]);

    // Handle selected location - zoom to it when explicitly requested or when not in search mode
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map || !selectedLocation || !window.google) return;

        // In search mode, only zoom if forceZoomToLocation is set ("Show on Map" action)
        if (searchPosition && !forceZoomToLocation) {
            return;
        }

        // Find the marker for this location and zoom to it
        if (allMarkersRef.current) {
            const marker = allMarkersRef.current.get(selectedLocation.uid);
            if (marker && marker.position) {
                // Center on marker and zoom in
                map.setCenter(marker.position as google.maps.LatLng);
                map.setZoom(useZoomlevel && selectedLocation.zoomlevel != null ? selectedLocation.zoomlevel : zoomSelectedLocation);
            }
        }
    }, [selectedLocation, searchPosition, forceZoomToLocation, useZoomlevel, zoomSelectedLocation]);

    return (
        <div
            ref={mapRef}
            style={{
                width: '100%',
                height: '100%',
                minHeight: '400px',
                position: 'relative',
                display: 'block',
            }}
        />
    );
};

// Temporarily remove React.memo to ensure proper re-renders
export default GoogleMap;
